import React, {
  Dispatch,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react'
import { AgGridReact } from '@ag-grid-community/react'
import {
  ColDef,
  GridApi,
  CellValueChangedEvent,
  GridOptions,
} from '@ag-grid-community/core'
import GridNoResultsOverlay from 'components/GridNoResultsOverlay/GridNoResultsOverlay'
import { ColumnConfig } from './types'
import { isNumericCellInvalid, isRowValid } from './helpers'
import { BulkUpdateTableCheckboxCellRenderer } from './BulkUpdateTableCheckboxCellRenderer'
import { BulkUpdateTableEditableCellRenderer } from './BulkUpdateTableEditableCellRenderer'
import { BulkUpdateTableCustomStatusBar } from './BulkUpdateTableCustomStatusBar'
import dayjs from 'dayjs'

export default function BulkUpdateTable<T extends Record<string, any>>(
  props: {
    onGridReady?: (api: GridApi) => void
    setData: Dispatch<React.SetStateAction<T[]>>
    data: T[]
    columns: ColumnConfig<T>[]
    setIsValid?: (isValid: boolean) => void
    style?: React.CSSProperties
    options?: {
      disableAddRow?: boolean
      disableEditable?: boolean
      disableDelete?: boolean
    }
  } & GridOptions<T>
) {
  const {
    onGridReady: onGridReadyProp,
    data,
    setData,
    columns,
    setIsValid,
    options,
    style,
  } = props
  /* --------------------------------- States --------------------------------- */
  const [_gridApi, setGridApi] = useState<GridApi | null>(null)

  useEffect(() => {
    if (setIsValid && columns.length) {
      const isValid = data.every((row) => isRowValid(row, columns))
      setIsValid(isValid)
    }
  }, [data, columns, setIsValid])

  /* --------------------------- Grid Configurations -------------------------- */
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      editable: true,
      suppressMovable: true,
      filterParams: {
        buttons: ['reset', 'apply'],
      },
    }),
    []
  )

  const handleDeleteRow = useCallback(
    (index: number) => {
      if (options?.disableDelete) return
      setData((prev) => {
        const newData = prev.filter((_, i) => i !== index)
        return newData
      })
    },
    [setData]
  )

  const handleCheckboxChange = useCallback(
    (rowIndex: number, colId: string, value: boolean) => {
      setData((prevData) => {
        const newData = [...prevData]
        newData[rowIndex] = {
          ...newData[rowIndex],
          [colId]: value,
        }
        return newData
      })
    },
    [setData]
  )

  const columnDefs = useMemo<ColDef<T>[]>(
    () => [
      {
        headerName: '#',
        valueGetter: (params) => {
          if (params.node?.rowPinned === 'bottom') return ''
          const rowIndex = params.node?.rowIndex
          return rowIndex !== undefined && rowIndex !== null ? rowIndex + 1 : ''
        },
        width: 70,
        pinned: 'left',
        sortable: false,
        filter: false,
      },
      ...columns.map(
        (column, index) =>
          ({
            ...column,
            editable:
              !options?.disableEditable &&
              (column.type !== 'boolean' || !!column.options?.length),
            minWidth: 200,
            cellEditor: column.options?.length
              ? 'agSelectCellEditor'
              : undefined,
            cellEditorParams: column.options?.length
              ? {
                  values: column.options.map((opt) => opt.value),
                }
              : undefined,
            cellRenderer: (params: {
              value: any
              data: T
              node: { rowIndex: number | null }
              column: { colId: string }
            }) => {
              const isLastColumn = index === columns.length - 1
              const rowIndex = params.node.rowIndex || 0
              let displayValue = params.value

              if (
                column.options?.length &&
                params.value !== null &&
                params.value !== undefined
              ) {
                const option = column.options.find(
                  (opt) => opt.value === params.value
                )
                if (option) {
                  displayValue = option.label
                }
              }

              if (column.type === 'boolean' && !column.options?.length) {
                return (
                  <BulkUpdateTableCheckboxCellRenderer
                    value={params.value}
                    rowIndex={rowIndex}
                    onDeleteRow={handleDeleteRow}
                    totalRows={data.length}
                    isLastColumn={isLastColumn}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        rowIndex,
                        params.column.colId,
                        checked
                      )
                    }
                  />
                )
              }

              if (column.type === 'date') {
                return <span>{dayjs(params.value).format('MM/DD/YYYY')}</span>
              }

              if (column.render)
                return column.render(displayValue, params.data, rowIndex)

              if (!options?.disableEditable) {
                return (
                  <BulkUpdateTableEditableCellRenderer
                    value={displayValue}
                    rowIndex={rowIndex}
                    onDeleteRow={handleDeleteRow}
                    totalRows={data.length}
                    isLastColumn={isLastColumn}
                  />
                )
              }

              return <span>{displayValue}</span>
            },
            suppressColumnsToolPanel: index === columns.length - 1,
            cellStyle: (params) => {
              if (index === 0 && !params.value) {
                return { border: '1px solid #ff4d4f' }
              }
              if (
                column.type === 'number' &&
                isNumericCellInvalid(params.value)
              ) {
                return { border: '1px solid #ff4d4f' }
              }
              return null
            },
          }) as ColDef<T>
      ),
    ],
    [columns, data.length, handleDeleteRow, handleCheckboxChange]
  )

  /* -------------------------------- Handlers -------------------------------- */
  const handleCellValueChanged = (params: CellValueChangedEvent<T>) => {
    if (params.node.rowIndex === null) return

    const colDef = params.colDef as ColumnConfig<T>

    // Parse numeric values before updating state
    if (colDef.type === 'number') {
      const numericValue = parseFloat(params.newValue)
      if (!isNaN(numericValue)) {
        const field = colDef.field as keyof T
        params.data[field] = numericValue as T[keyof T]
      }
    }

    const updatedData = [...data]
    updatedData[params.node.rowIndex] = params.data
    setData(updatedData)
  }

  const onGridReady = (params: { api: GridApi }) => {
    const { api } = params
    setGridApi(api)
    onGridReadyProp?.(api)
  }

  const handleAddRow = () => {
    if (options?.disableAddRow) return
    setData((prev) => {
      const emptyRow = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.field]: '',
        }),
        {} as T
      )
      return [...prev, emptyRow]
    })
  }

  /* ---------------------------------- Doms ---------------------------------- */

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        {
          statusPanel: BulkUpdateTableCustomStatusBar,
          key: 'customStatusBar',
          statusPanelParams: {
            onAddRow: handleAddRow,
            disableAddRow: options?.disableAddRow,
          },
        },
      ],
    }),
    [options?.disableAddRow]
  )

  return (
    <div
      className="ag-theme-alpine"
      style={{ height: '600px', width: '100%', ...style }}
    >
      <AgGridReact
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        rowData={data}
        noRowsOverlayComponent={GridNoResultsOverlay}
        tooltipShowDelay={100}
        suppressMenuHide
        onGridReady={onGridReady}
        onCellValueChanged={handleCellValueChanged}
        stopEditingWhenCellsLoseFocus
        statusBar={statusBar}
        rowSelection="multiple"
        suppressRowClickSelection
        {...props}
      />
    </div>
  )
}
